import {
  BrowserAuthError,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { trackEvent } from '@dispatch/web/telemetry';
import { clearAuthorityFromStorage } from './AuthorityStorage';
import { clearEmailFromStorage } from './EmailStorage';

/**
 * Bounded delay (ms) before retrying a logout redirect that failed because an
 * MSAL interaction was already in progress. Short enough to stay imperceptible,
 * long enough to let the in-flight interaction settle.
 */
const INTERACTION_RETRY_DELAY_MS = 400;

/**
 * MSAL error code emitted by `logoutRedirect` when another interaction (e.g. a
 * concurrent token acquisition) is still in progress.
 */
const INTERACTION_IN_PROGRESS_CODE = 'interaction_in_progress';

/**
 * MSAL keeps its account/token cache in `localStorage` (see AuthConfig.base.ts:
 * `cacheLocation: 'localStorage'`), but temporary state — including this
 * interaction lock — is ALWAYS kept in `sessionStorage`
 * (@azure/msal-browser@2.26.0 `BrowserCacheManager.setupTemporaryCacheStorage`).
 * `logoutRedirect` throws `interaction_in_progress` synchronously from its
 * preflight when this key is set, and does NOT clear it on that throw, so a
 * blind retry throws identically. We evict it ourselves before retrying.
 */
export const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status';

function isInteractionInProgressError(error: unknown): boolean {
  return (
    error instanceof BrowserAuthError &&
    error.errorCode === INTERACTION_IN_PROGRESS_CODE
  );
}

/**
 * Extracts a stable MSAL error code for telemetry, falling back to 'unknown'
 * when the thrown value is not a recognized `BrowserAuthError`.
 */
const errorCodeOf = (error: unknown): string =>
  error instanceof BrowserAuthError ? error.errorCode : 'unknown';

/**
 * Standardized logout function that ensures consistent cleanup across the
 * application. Awaits `logoutRedirect` so callers can sequence re-auth
 * deterministically. If MSAL reports an interaction is already in progress the
 * redirect is retried once after a short bounded delay; a persistent failure
 * emits a `LogoutRedirectFailed` telemetry event (never rethrown into the UI)
 * so the stranded-user condition is observable in PROD.
 * @param instance - MSAL instance to perform the logout
 * @param postLogoutRedirectUri - Optional URI to redirect to after logout (defaults to '/sign-in')
 * @returns `true` once a redirect has been initiated, `false` if the user was
 * left stranded (both attempts failed) so callers can apply a hard fallback.
 */
export async function performLogout(
  instance: IPublicClientApplication,
  postLogoutRedirectUri = '/sign-in'
): Promise<boolean> {
  // Clear all stored authentication data
  clearAuthorityFromStorage();
  clearEmailFromStorage();

  try {
    // logoutRedirect is terminal on success — it navigates the browser away,
    // so no post-redirect logic is required.
    await instance.logoutRedirect({ postLogoutRedirectUri });
    return true;
  } catch (error) {
    if (isInteractionInProgressError(error)) {
      // A concurrent interaction blocked the redirect. Wait briefly for it to
      // settle, then retry exactly once.
      await new Promise((resolve) =>
        setTimeout(resolve, INTERACTION_RETRY_DELAY_MS)
      );
      // MSAL does not clear the interaction lock on the synchronous
      // preflight throw, so evict it ourselves — otherwise the retry
      // re-evaluates the same set key and throws identically.
      sessionStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);
      try {
        await instance.logoutRedirect({ postLogoutRedirectUri });
        return true;
      } catch (retryError) {
        // Retry also failed: the user is stranded (no redirect). Emit telemetry
        // so this is observable in PROD, where console output is invisible.
        trackEvent('LogoutRedirectFailed', {
          errorCode: errorCodeOf(retryError),
          phase: 'retry',
        });
        return false;
      }
    }

    // Non-interaction failure on the first attempt: the user is stranded.
    // Emit telemetry so this is observable in PROD.
    trackEvent('LogoutRedirectFailed', {
      errorCode: errorCodeOf(error),
      phase: 'initial',
    });
    return false;
  }
}

/**
 * Logs out the user due to missing authentication requirements.
 * @param instance - MSAL instance to perform the logout
 * @param reason - Reason for the logout (for logging purposes)
 * @returns `true` once a redirect has been initiated, `false` if stranded.
 */
export async function performErrorLogout(
  instance: IPublicClientApplication,
  reason: string
): Promise<boolean> {
  // eslint-disable-next-line no-console
  console.error('[Auth] Logout triggered:', reason);
  return performLogout(instance, '/sign-in');
}
